/**
 * Meta (Facebook) Pixel loader.
 * Loads the FB pixel script once per page load, fires a PageView,
 * and exposes fbq-compatible event helpers.
 *
 * Scoped so each page that wants the pixel can opt in via useFacebookPixel().
 */

let scriptInjected = false;
// Track which pixel IDs we've already init'd so React Strict Mode (double effect)
// or repeated mounts don't trigger Meta's "Duplicate Pixel ID" warning.
const initializedPixels = new Set();

/**
 * Inject the Meta Pixel script into the document head (idempotent).
 * Returns true if the script is available (either newly loaded or already present).
 */
const injectPixelScript = () => {
  if (typeof window === 'undefined') return false;
  if (window.fbq) return true;
  if (scriptInjected) return true;

  // eslint-disable-next-line
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  scriptInjected = true;
  return true;
};

/**
 * Initialize a pixel ID and fire a PageView.
 * Guarded against duplicate init from React Strict Mode, hot reload, and
 * cross-page navigation (where the module's `initializedPixels` Set gets
 * reset but the global `window.fbq` state persists).
 */
export const initPixel = (pixelId) => {
  if (!pixelId) return;
  if (!injectPixelScript()) return;
  if (initializedPixels.has(pixelId)) return;

  // Also check Meta's own internal pixel registry on `window.fbq.instance`
  // so we don't re-init a pixel that was already initialized by an earlier
  // page mount in the same browser session.
  try {
    const alreadyOnFbq =
      window.fbq &&
      window.fbq.instance &&
      window.fbq.instance.pixelsByID &&
      Object.prototype.hasOwnProperty.call(window.fbq.instance.pixelsByID, pixelId);
    if (alreadyOnFbq) {
      initializedPixels.add(pixelId);
      // Still fire PageView for SPA route changes — that's the whole point of
      // calling initPixel again — but skip the duplicate `init` call.
      window.fbq('track', 'PageView');
      return;
    }
  } catch (_) {
    /* fall through to standard init */
  }

  initializedPixels.add(pixelId);
  try {
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  } catch (_) {
    /* noop */
  }
};

/**
 * Fire a standard or custom Meta event.
 * Example: fbqTrack('Lead', { value: 0, currency: 'USD' })
 */
export const fbqTrack = (eventName, params) => {
  if (typeof window === 'undefined' || !window.fbq || !eventName) return;
  try {
    if (params) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
  } catch (_) {
    /* noop */
  }
};

/**
 * Append the <noscript> fallback pixel to the document body (idempotent).
 * Uses a 1x1 tracking <img>; renders for users with JS disabled.
 */
export const mountNoScriptFallback = (pixelId) => {
  if (typeof document === 'undefined' || !pixelId) return;
  const id = `fb-pixel-noscript-${pixelId}`;
  if (document.getElementById(id)) return;
  const noscript = document.createElement('noscript');
  noscript.id = id;
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
};
